import { useCallback, useEffect, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

/**
 * Custom hook to manage authentication token retrieval and redirection using Auth0.
 */
export const useAuthManager = () => {
  const auth0 = useAuth0();
  const [accessToken, setAccessToken] = useState<string>("");
  const [isTokenReady, setIsTokenReady] = useState(false);

  /**
   * Silently gets an access token from Auth0.
   */
  const getAccessToken = useCallback(
    async (options?: { forceRefresh?: boolean }) => {
      if (!auth0.isAuthenticated) return undefined;

      try {
        const token = await auth0.getAccessTokenSilently({
          authorizationParams: { scope: "openid profile email offline_access" },
          cacheMode: options?.forceRefresh ? "off" : "on",
        });

        // Update local state if it changed
        if (token !== accessToken) {
          setAccessToken(token);
        }

        return token;
      } catch (error) {
        console.error("Failed to get access token:", error);
        return undefined;
      }
    },
    [auth0, accessToken]
  );

  /**
   * Initialization effect to either fetch the token or redirect to login.
   */
  useEffect(() => {
    const fetchAccessToken = async () => {
      if (auth0.isAuthenticated) {
        try {
          const token = await getAccessToken();
          if (token) {
            setIsTokenReady(true);
            return;
          }

          setIsTokenReady(false);
          await auth0.loginWithRedirect({
            appState: {
              returnTo: window.location.pathname,
            },
          });
        } catch (error) {
          console.error("Failed to get access token during initialization:", error);
          setIsTokenReady(false);
          await auth0.loginWithRedirect({
            appState: {
              returnTo: window.location.pathname,
            },
          });
        }
      } else if (!auth0.isLoading) {
        // Only redirect if not on excluded paths (like public invite links or registration)
        const shouldRedirect =
          window.location.pathname !== "/register" &&
          !window.location.href.includes("/invite") &&
          !window.location.href.includes("/reset");

        if (shouldRedirect) {
          auth0.loginWithRedirect({
            appState: {
              returnTo: window.location.pathname,
            },
          });
        }
      }
    };

    fetchAccessToken();
  }, [auth0, auth0.isAuthenticated, auth0.isLoading, getAccessToken]);

  return {
    auth0,
    accessToken,
    setAccessToken,
    isTokenReady,
    getAccessToken,
  };
};
